import { useCallback, useRef, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useTranslation } from '@frontend/i18n';
import { useFormField } from '@frontend/design-system';
import type { PaymentMethodSetupGatewayProps } from '../context/payment-method-setup-base.context';
import { fetchSavedPaymentMethod, getPaymentMethodId } from '../utils/poll-payment-method';
import { getGenericSetupErrorMessage, getSetupErrorMessage } from '../utils/setup-error-message';
import { buildSetupReturnUrl, saveSetupCardholderName } from '../utils/setup-redirect';

export const usePaymentMethodSetupStripe = ({
  kind,
  api,
  session,
  setupKey,
  onSaved,
  onError,
  onEvent,
}: PaymentMethodSetupGatewayProps) => {
  const { t } = useTranslation('payments');
  const stripe = useStripe();
  const elements = useElements();
  const nameFieldProps = useFormField({ type: 'text', required: true });
  const [isElementComplete, setIsElementComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // A confirmed SetupIntent can't be confirmed again, so a failed lookup retries with this id alone.
  const confirmedMethodIdRef = useRef<string>();

  const isCard = kind === 'card';
  const nameOnCard = nameFieldProps.value.trim();

  const onElementChange = useCallback(
    (event: StripePaymentElementChangeEvent) => setIsElementComplete(event.complete),
    []
  );

  const fail = useCallback(
    (message: string, code: string) => {
      onEvent('save_error', { code });
      onError(message, code);
    },
    [onError, onEvent]
  );

  const save = useCallback(async () => {
    if (isCard && !nameOnCard) {
      onError(t('Cardholder name is required!'), 'missing_name');
      return;
    }

    if (!stripe || !elements) {
      fail(getGenericSetupErrorMessage(kind, t), 'processor_not_ready');
      return;
    }

    setIsSaving(true);
    onEvent('save_attempt');

    try {
      if (!confirmedMethodIdRef.current) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          fail(
            submitError.message ?? t('Error submitting details to Stripe. Please review information and try again.'),
            submitError.code ?? 'element_submit'
          );
          return;
        }

        if (isCard) saveSetupCardholderName(setupKey, nameOnCard);
        const result = await stripe.confirmSetup({
          elements,
          clientSecret: session.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: buildSetupReturnUrl(setupKey),
            ...(isCard && { payment_method_data: { billing_details: { name: nameOnCard } } }),
          },
        });

        if (result.error) {
          fail(getSetupErrorMessage(kind, result.error.code, t), result.error.code ?? 'confirm_setup');
          return;
        }

        confirmedMethodIdRef.current = getPaymentMethodId(result.setupIntent.payment_method);
      }

      const paymentMethodId = confirmedMethodIdRef.current;
      if (!paymentMethodId) {
        fail(t('Payment method not found!'), 'missing_payment_method');
        return;
      }

      const { method, error } = await fetchSavedPaymentMethod({
        getPaymentMethod: api.getPaymentMethod,
        paymentMethodId,
        kind,
        onRetry: (attempt) => onEvent('details_fetch_retry', { attempt }),
      });

      if (error === 'details_not_found') {
        fail(t('Payment method not found!'), error);
        return;
      }
      if (error) {
        fail(isCard ? t('Unable to get card details!') : t('Unable to get ACH details!'), error);
        return;
      }

      onEvent('save_success');
      onSaved(method.kind === 'card' ? { ...method, nameOnCard } : method);
    } catch {
      fail(getGenericSetupErrorMessage(kind, t), 'unexpected');
    } finally {
      setIsSaving(false);
    }
  }, [
    api.getPaymentMethod,
    elements,
    fail,
    isCard,
    kind,
    nameOnCard,
    onError,
    onEvent,
    onSaved,
    session,
    setupKey,
    stripe,
    t,
  ]);

  const canSave = !!stripe && !!elements && isElementComplete && (!isCard || !!nameOnCard);

  return { save, isSaving, canSave, nameFieldProps, onElementChange };
};
